import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import offlineStorage from '../../../utils/offlineStorage';

const DigitalTickets = ({ tickets }) => {
  const navigate = useNavigate();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [offlineTickets, setOfflineTickets] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Save tickets to offline storage
    const saveTicketsOffline = async () => {
      await offlineStorage?.saveTickets(tickets);
    };
    
    if (tickets?.length > 0) {
      saveTicketsOffline();
    }

    // Load offline tickets if offline
    const loadOfflineTickets = async () => {
      if (!navigator.onLine) {
        const cached = await offlineStorage?.getTickets();
        setOfflineTickets(cached);
      }
    };

    loadOfflineTickets();

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
      loadOfflineTickets();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [tickets]);

  const displayTickets = isOnline ? tickets : offlineTickets;

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500 text-white',
      upcoming: 'bg-yellow-500 text-white',
      expired: 'bg-gray-500 text-white',
      cancelled: 'bg-red-500 text-white',
      rejected: 'bg-red-600 text-white'
    };
    return colors?.[status] || 'bg-gray-400 text-white';
  };

  const getStatusText = (status) => {
    const texts = {
      active: 'Onaylandı',
      upcoming: 'Beklemede',
      expired: 'Süresi Dolmuş',
      cancelled: 'İptal Edildi',
      rejected: 'Reddedildi'
    };
    return texts?.[status] || status;
  };

  const handleViewEvent = (eventId) => {
    // Navigate to actual event details page with event ID
    navigate(`/event-details/${eventId}`);
  };

  const handleViewAllTickets = () => {
    // Navigate to My Tickets page for full ticket management
    navigate('/my-tickets');
  };

  return (
    <div className="bg-card rounded-xl shadow-organic p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
          <Icon name="Ticket" size={24} />
          Dijital Biletlerim
          {!isOnline && (
            <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full">
              Çevrimdışı
            </span>
          )}
        </h2>
        {displayTickets?.length > 0 && (
          <button
            onClick={handleViewAllTickets}
            className="text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
          >
            Tümünü Gör →
          </button>
        )}
      </div>
      {displayTickets?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Ticket" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground mb-4">Henüz biletiniz bulunmuyor</p>
          <button
            onClick={() => navigate('/event-discovery')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-smooth font-medium"
          >
            Etkinlikleri Keşfet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {displayTickets?.map((ticket) => (
            <div
              key={ticket?.id}
              className="bg-background rounded-lg border border-border overflow-hidden hover:shadow-organic transition-smooth cursor-pointer"
              onClick={() => setSelectedTicket(ticket)}
            >
              <div className="relative h-32 md:h-40 overflow-hidden">
                <Image
                  src={ticket?.eventImage}
                  alt={ticket?.eventImageAlt}
                  className="w-full h-full object-cover"
                />
                {/* Status badge on top-left corner */}
                <div className={`absolute top-3 left-3 ${getStatusColor(ticket?.status)} px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg`}>
                  {getStatusText(ticket?.status)}
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 line-clamp-1">
                  {ticket?.eventName}
                </h3>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Icon name="Calendar" size={14} />
                    <span>{ticket?.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} />
                    <span className="line-clamp-1">{ticket?.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Icon name="Users" size={14} />
                    <span>{ticket?.ticketType}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
                  <div className="text-xs md:text-sm">
                    <span className="text-muted-foreground">Bilet No: </span>
                    <span className="font-mono font-semibold text-foreground">{ticket?.ticketNumber}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedTicket(ticket);
                    }}
                  >
                    Detaylar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedTicket && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTicket(null)}
        >
          <div
            className="bg-card rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Ticket" size={20} className="text-primary" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">Bilet Detayları</h3>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Event Image */}
            <div className="relative mb-6 rounded-xl overflow-hidden">
              <Image
                src={selectedTicket?.eventImage}
                alt={selectedTicket?.eventImageAlt}
                className="w-full h-48 object-cover"
              />
              <div className={`absolute top-3 left-3 ${getStatusColor(selectedTicket?.status)} px-4 py-2 rounded-lg text-sm font-bold shadow-lg`}>
                {getStatusText(selectedTicket?.status)}
              </div>
            </div>

            {/* Event Name */}
            <h4 className="text-lg md:text-xl font-bold text-foreground mb-6">{selectedTicket?.eventName}</h4>

            {/* Ticket Information Grid */}
            <div className="space-y-4 mb-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Calendar" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Etkinlik Tarihi</p>
                    <p className="text-sm font-semibold text-foreground">{selectedTicket?.date}</p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPin" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Konum</p>
                    <p className="text-sm font-semibold text-foreground">{selectedTicket?.location}</p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Users" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Bilet Tipi</p>
                    <p className="text-sm font-semibold text-foreground">{selectedTicket?.ticketType}</p>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Hash" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Bilet Numarası</p>
                    <p className="text-base font-mono font-bold text-foreground">{selectedTicket?.ticketNumber}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-6 mb-6">
              <p className="text-sm text-center text-muted-foreground mb-4">Etkinlik girişinde gösterilecek QR kod</p>
              <div className="bg-white p-6 rounded-xl inline-block w-full">
                <div className="w-full aspect-square bg-muted flex items-center justify-center rounded-lg">
                  <Icon name="QrCode" size={120} className="text-foreground" />
                </div>
              </div>
              <p className="text-xs text-center text-muted-foreground mt-4">
                Bu QR kodu etkinlik organizatörüne göstererek giriş yapabilirsiniz
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                iconName="ArrowLeft"
                onClick={() => setSelectedTicket(null)}
              >
                Kapat
              </Button>
              <Button
                variant="default"
                className="flex-1"
                iconName="ExternalLink"
                onClick={() => {
                  navigate(`/event-details/${selectedTicket?.eventId}`);
                  setSelectedTicket(null);
                }}
              >
                Etkinliği Görüntüle
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalTickets;